import { newId } from '../domain/id.ts';
import type { MemberKind } from '../ports/auth-port.ts';
import type { Notification, RecordRef } from '../domain/entities/notification.ts';
import { NotificationSchema } from '../domain/entities/notification.ts';
import type { ClockPort } from '../ports/clock-port.ts';
import { createWrite, updateWrite, type BatchWrite } from '../ports/batch.ts';
import type { DataPort } from '../ports/data-port.ts';
import type { Unsubscribe } from '../ports/query.ts';
import { defaultChannels, notificationEvent, reaches } from './events.ts';

/**
 * Raising a notification.
 *
 * Writes are produced rather than committed, so a notification lands in the
 * same batch as the change it describes. That is the whole reason this is
 * shaped as `notificationWrites` and not `sendNotification`: telling
 * somebody dates moved when the move itself failed to commit is worse than
 * not telling them, and the two cannot be kept consistent across two
 * commits.
 *
 * The batch budget is real and has already shaped one decision here — see
 * `docs/features/02-schedule.md` §4.1 — so a caller that may touch hundreds
 * of records must count its recipients, not assume.
 */
export interface NotificationRecipient {
  readonly uid: string;
  readonly kind: MemberKind;
}

export interface RaiseNotification {
  readonly event: string;
  readonly recipients: readonly NotificationRecipient[];
  readonly title: string;
  readonly body?: string;
  readonly recordRef: RecordRef;
  readonly actorUid?: string;
  readonly actorName?: string;
}

export class NotificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotificationError';
  }
}

/**
 * Batch writes for one event, one per recipient who can receive it.
 *
 * A recipient the event does not address is skipped rather than written with
 * no channels: a row nobody will ever read is still a row somebody has to
 * explain. The actor is skipped too — being told about your own action is
 * noise, and it is the commonest way a notification feed becomes ignorable.
 *
 * Duplicate recipients collapse. A sub assigned to an item twice, or named
 * both individually and through a group, is still one person.
 */
export function notificationWrites(
  context: { readonly companyId: string; readonly clock: ClockPort },
  raise: RaiseNotification,
): BatchWrite[] {
  const event = notificationEvent(raise.event);
  if (!event) {
    // A typo in an event key would otherwise send nothing, silently, which
    // is indistinguishable from the feature working and nobody caring.
    throw new NotificationError(`Unknown notification event "${raise.event}"`);
  }

  const at = context.clock.now().toISOString();
  const seen = new Set<string>();
  const writes: BatchWrite[] = [];

  for (const recipient of raise.recipients) {
    if (recipient.uid === raise.actorUid) continue;
    if (seen.has(recipient.uid)) continue;
    seen.add(recipient.uid);
    if (!reaches(event, recipient.kind)) continue;

    const notification: Notification = {
      id: newId(),
      companyId: context.companyId,
      recipientUid: recipient.uid,
      event: event.key,
      title: raise.title,
      body: raise.body,
      recordRef: raise.recordRef,
      actorUid: raise.actorUid,
      actorName: raise.actorName,
      at,
      channels: [...defaultChannels(event, recipient.kind)],
    };

    // Validated before it is stored, like every other document crossing the
    // boundary: a malformed notification is one nobody can route from.
    const checked = NotificationSchema.safeParse(notification);
    if (!checked.success) throw new NotificationError(checked.error.issues[0]!.message);

    writes.push(
      createWrite(
        { companyId: context.companyId, userId: recipient.uid },
        'notifications',
        notification,
      ),
    );
  }

  return writes;
}

export interface NotificationContext {
  readonly data: DataPort;
  readonly companyId: string;
  readonly uid: string;
}

function inbox(context: NotificationContext) {
  return context.data.collection<Notification>(
    { companyId: context.companyId, userId: context.uid },
    'notifications',
  );
}

/** Your own notifications, newest first. */
export async function listNotifications(
  context: NotificationContext,
  limit = 50,
): Promise<Notification[]> {
  const page = await inbox(context).list({
    orderBy: [{ field: 'at', direction: 'desc' }],
    limit,
  });
  return [...page.items];
}

/** Your own notifications, live, for a bell that updates without a reload. */
export function subscribeToNotifications(
  context: NotificationContext,
  onChange: (items: readonly Notification[]) => void,
  onError: (cause: Error) => void,
  limit = 50,
): Unsubscribe {
  return inbox(context).subscribe(
    { orderBy: [{ field: 'at', direction: 'desc' }], limit },
    (page) => onChange(page.items),
    onError,
  );
}

export function unreadCount(items: readonly Notification[]): number {
  return items.filter((item) => item.readAt === undefined).length;
}

/**
 * Mark one as read.
 *
 * Idempotent: an already-read notification keeps the time it was first read
 * rather than being stamped again, so "when did they see this" stays
 * answerable after a second visit.
 */
export async function markNotificationRead(
  context: NotificationContext & { readonly clock: ClockPort },
  id: string,
): Promise<void> {
  const repo = inbox(context);
  const existing = await repo.get(id);
  if (!existing || existing.readAt !== undefined) return;
  await repo.update(id, { readAt: context.clock.now().toISOString() });
}

/**
 * Mark everything currently unread as read, in one batch.
 *
 * Takes the items the caller is showing rather than re-reading: the point of
 * "mark all read" is to clear what is on screen, and re-reading could sweep
 * away something that arrived in between and was never seen.
 */
export async function markAllRead(
  context: NotificationContext & { readonly clock: ClockPort },
  items: readonly Notification[],
): Promise<number> {
  const unread = items.filter((item) => item.readAt === undefined);
  if (unread.length === 0) return 0;

  const at = context.clock.now().toISOString();
  const scope = { companyId: context.companyId, userId: context.uid };
  await context.data.commit(
    unread.map((item) =>
      updateWrite<Notification>(scope, 'notifications', item.id, { readAt: at }),
    ),
  );
  return unread.length;
}
